import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Send, Bell, Users, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import { Procurement } from '@/types/database.types';
import { 
  sendTelegramNotification, 
  sendInteractiveMessage,
  scheduleNotification,
  formatNewProcurementNotification,
  formatApprovedProcurementNotification,
  formatRejectedProcurementNotification,
  formatStatusChangeNotification,
  formatPaymentReminderNotification,
  formatDeliveryNotification,
  formatQualityVerificationNotification
} from '@/services/telegramService';
import { useToast } from '@/hooks/use-toast';
import { TimePicker } from '../ui/time-picker';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ProcurementTelegramNotifyProps {
  procurement: Procurement;
}

export function ProcurementTelegramNotify({ procurement }: ProcurementTelegramNotifyProps) {
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState('status');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(undefined);
  const [scheduledTime, setScheduledTime] = useState<string>("12:00");
  const [recipientType, setRecipientType] = useState('all');
  const [isInteractive, setIsInteractive] = useState(false);
  const { toast } = useToast();
  
  // Query to get finance users
  const { data: financeUsers } = useQuery({
    queryKey: ['users', 'finance'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name')
        .eq('role', 'finance');
      
      if (error) {
        console.error('Error fetching finance users:', error);
        return [];
      }
      
      return data || [];
    }
  });
  
  // Query to get telegram groups
  const { data: telegramGroups } = useQuery({
    queryKey: ['telegram', 'groups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('telegram_groups')
        .select('chat_id, chat_title')
        .eq('is_active', true);
      
      if (error) {
        console.error('Error fetching telegram groups:', error);
        return [];
      }
      
      return data || [];
    }
  });
  
  const formatStatusMessage = () => {
    return formatNewProcurementNotification(procurement);
  };
  
  const formatPaymentMessage = () => {
    const dueDate = procurement.finance?.payment_due_date ? new Date(procurement.finance.payment_due_date) : new Date();
    const today = new Date();
    const daysRemaining = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
    
    // Payment reminders are always text-only now
    return formatPaymentReminderNotification(procurement, daysRemaining);
  };
  
  const formatQualityMessage = () => {
    return formatQualityVerificationNotification(procurement);
  };

  const formatDeliveryMessage = () => {
    return formatDeliveryNotification(procurement);
  };

  // Generate the message template based on the selected notification type
  const generateTemplate = () => {
    switch (notificationType) {
      case 'status':
        setMessage(formatStatusMessage());
        break;
      case 'payment':
        setMessage(formatPaymentMessage());
        // Disable interactive mode for payment reminders
        setIsInteractive(false);
        break;
      case 'quality':
        setMessage(formatQualityMessage());
        break;
      case 'delivery':
        setMessage(formatDeliveryMessage());
        break;
      case 'custom':
        setMessage('');
        break;
      default:
        setMessage('');
    }
  };
  
  // Effect to update message when notification type changes
  React.useEffect(() => {
    generateTemplate();
  }, [notificationType]);
  
  const getScheduledDateTime = (): Date | undefined => {
    if (!isScheduled || !scheduledDate) return undefined;
    
    const [hours, minutes] = scheduledTime.split(':').map(Number);
    const dateTime = new Date(scheduledDate);
    dateTime.setHours(hours, minutes);
    return dateTime;
  };
  
  const getRecipientIds = (): string[] | undefined => {
    if (recipientType === 'all') return undefined;
    
    // If finance users are selected
    if (recipientType === 'finance' && financeUsers) {
      return financeUsers.map(user => user.id);
    }
    
    // For other recipient types, we could add more logic here
    return undefined;
  };
  
  const getInteractiveButtons = () => {
    // Payment reminders should never have interactive buttons
    if (!isInteractive || notificationType === 'payment') return undefined;
    
    switch (notificationType) {
      case 'status':
        return [
          { text: "✅ Approve", action: "approve", data: procurement.id },
          { text: "❌ Reject", action: "reject", data: procurement.id },
          { text: "🔍 View Details", action: "view", data: procurement.id }
        ];
      default:
        return [
          { text: "🔍 View Details", action: "view", data: procurement.id }
        ];
    }
  };
  

  const handleSendNotification = async () => {
    if (!message.trim()) {
      toast({
        title: "Message Empty",
        description: "Please enter a message to send.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSending(true);
    
    try {
      // Get scheduled date/time if applicable
      const scheduledDateTime = getScheduledDateTime();
      
      // If scheduled for future, use scheduling function
      if (isScheduled && scheduledDateTime) {
        const now = new Date();
        if (scheduledDateTime < now) {
          toast({
            title: "Invalid Schedule Date",
            description: "Please select a future date for scheduling.",
            variant: "destructive"
          });
          setIsSending(false);
          return;
        }
        
        const recipients = getRecipientIds();
        const buttons = getInteractiveButtons();
        
        // Schedule the notification
        const result = await scheduleNotification(
          message,
          scheduledDateTime,
          procurement.id,
          recipients,
          buttons
        );
        
        if (result.success) {
          // Success message is shown by the scheduleNotification function
          setIsSending(false);
          return;
        } else {
          toast({
            title: "Failed to Schedule",
            description: "There was an issue scheduling the notification.",
            variant: "destructive"
          });
          setIsSending(false);
          return;
        }
      }
      
      let result;
      
      // For interactive messages (but not payment reminders)
      if (isInteractive && notificationType !== 'payment') {
        const buttons = getInteractiveButtons() || [];
        result = await sendInteractiveMessage(message, buttons, undefined, procurement.id);
      } else {
        // For regular notifications
        const recipients = getRecipientIds();
        result = await sendTelegramNotification(
          message, 
          procurement.id,
          recipients
        );
      }
      
      if (result.success) {
        toast({
          title: "Notification Sent",
          description: `Successfully sent to ${result.sent} recipients.`
        });
      } else {
        toast({
          title: "Failed to Send",
          description: "There was an issue sending the notification.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while sending the notification.",
        variant: "destructive"
      });
    } finally {
      setIsSending(false);
    }
  };

  // Check if interactive mode should be disabled
  const isInteractiveDisabled = notificationType === 'payment';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Send Telegram Notification
        </CardTitle>
        <CardDescription>
          Notify relevant personnel about this procurement via Telegram
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="notification-type">Notification Type</Label>
            <Select 
              value={notificationType} 
              onValueChange={setNotificationType}
            >
              <SelectTrigger className="w-full" id="notification-type">
                <SelectValue placeholder="Select notification type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Notification Templates</SelectLabel>
                  <SelectItem value="status">Status Update</SelectItem>
                  <SelectItem value="payment">Payment Reminder</SelectItem>
                  <SelectItem value="quality">Quality Verification</SelectItem>
                  <SelectItem value="delivery">Delivery Notification</SelectItem>
                  <SelectItem value="custom">Custom Message</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="recipient-type">Recipients</Label>
            <Select 
              value={recipientType} 
              onValueChange={setRecipientType}
            >
              <SelectTrigger className="w-full" id="recipient-type">
                <SelectValue placeholder="Select recipients" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Who should receive this?</SelectLabel>
                  <SelectItem value="all">All relevant personnel</SelectItem>
                  <SelectItem value="finance">Finance team</SelectItem>
                  <SelectItem value="quality">Quality control</SelectItem>
                  <SelectItem value="logistics">Logistics team</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Switch
            id="interactive"
            checked={isInteractive}
            onCheckedChange={setIsInteractive}
            disabled={isInteractiveDisabled}
          />
          <Label htmlFor="interactive" className={cn("cursor-pointer", isInteractiveDisabled && "text-muted-foreground")}>
            Use interactive buttons {isInteractiveDisabled && "(disabled for payment reminders)"}
          </Label>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="message">Message</Label>
            <span className="text-xs text-muted-foreground">
              Supports Markdown formatting
            </span>
          </div>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter notification message..."
            className="min-h-[150px] font-mono text-sm"
          />
        </div>
        
        <div className="flex flex-col space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="schedule"
              checked={isScheduled}
              onCheckedChange={setIsScheduled}
            />
            <Label htmlFor="schedule" className="cursor-pointer">Schedule for later</Label>
          </div>
          
          {isScheduled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col space-y-2">
                <Label htmlFor="schedule-date">Schedule Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !scheduledDate && "text-muted-foreground"
                      )}
                      id="schedule-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {scheduledDate ? format(scheduledDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={scheduledDate}
                      onSelect={setScheduledDate}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="flex flex-col space-y-2">
                <Label htmlFor="schedule-time">Schedule Time</Label>
                <TimePicker
                  setTime={setScheduledTime}
                  time={scheduledTime}
                />
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center bg-muted/50 p-3 rounded-md">
          <Users className="h-5 w-5 mr-2 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {recipientType === 'all' 
              ? "This notification will be sent to all relevant Telegram groups and users"
              : `This notification will be sent to the ${recipientType} team`
            }
          </span>
        </div>
      </CardContent>
      
      <CardFooter>
        <Button 
          onClick={handleSendNotification} 
          disabled={isSending || !message.trim()}
          className="w-full"
        >
          {isSending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isScheduled ? 'Scheduling...' : 'Sending...'}
            </>
          ) : (
            <>
              {isScheduled ? (
                <>
                  <Clock className="mr-2 h-4 w-4" />
                  Schedule Notification
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send Notification
                </>
              )}
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
